/**
 * Shared pieces of the invite → purchase flow.
 *
 * Both invite designs (V4, V5) and the claim wizard render from here so that
 * pricing copy, plan wording and the demo fixtures cannot drift between them.
 */

import React from 'react';
import type { PlanId } from '../../lib/pricing';

/* ------------------------------------------------------------------ */
/* Shapes                                                              */
/* ------------------------------------------------------------------ */

export interface InviteWebsite {
  domain: string;
  screenshotUrl: string | null;
  previewUrl: string;
  businessName?: string;
  businessPhone?: string;
  /**
   * The owner, as a person — set only when we actually enriched one. Distinct
   * from `InviteInfo.name`, which the pipeline fills with the *business* name;
   * greeting copy runs both through `personalFirstName` and falls back to a
   * written unnamed variant rather than guessing.
   */
  contactName?: string;
  trade?: string;
  city?: string;
  siteCategory?: 'no_site' | 'weak_site';
  oldSiteUrl?: string;
}

export interface InviteInfo {
  email: string;
  name: string;
  /** null = the invite hasn't been sent yet — its expiry clock hasn't started. */
  expiresAt: string | null;
  businessName?: string;
  website?: InviteWebsite;
}

export type InviteStatus =
  | 'loading'
  | 'preview'
  | 'activate'
  // fnd-k1d.4: the emailed confirmation code, between filling the claim form
  // and the account actually being created.
  | 'confirm'
  | 'activating'
  | 'success'
  | 'invalid'
  | 'expired'
  | 'used';

export type ContactPreference = 'email' | 'text' | 'call';

export const DEMO_TOKENS = ['demo-no-site', 'demo-weak-site'] as const;
export type DemoToken = (typeof DEMO_TOKENS)[number];

export function isDemoToken(token?: string): token is DemoToken {
  return DEMO_TOKENS.includes(token as DemoToken);
}

/**
 * Fixtures for the two shapes of invite we actually send. Every asset they
 * reference is served from this app's own `public/`, so the prototype opens
 * with one dev server running and never depends on the marketing site's.
 */
export const DEMO_INVITES: Record<DemoToken, InviteInfo> = {
  'demo-no-site': {
    email: 'republic-plumbing@placeholder.foundlio.com',
    name: 'Sarah Mitchell',
    expiresAt: new Date(Date.now() + 5 * 86400000).toISOString(),
    businessName: 'Republic Plumbing',
    website: {
      domain: 'republic-plumbing.foundlio.com',
      screenshotUrl: '/demo-shots/valley-plumbing.png',
      previewUrl: 'http://localhost:3003/demos',
      businessName: 'Republic Plumbing',
      businessPhone: '(608) 555-0188',
      // Enriched owner — this fixture previews the named copy.
      contactName: 'Sarah Mitchell',
      trade: 'plumbing',
      city: 'Madison',
      siteCategory: 'no_site',
    },
  },
  'demo-weak-site': {
    email: 'daniel@crestlineroofing.com',
    // No owner was ever enriched, so the pipeline sent the business name —
    // exactly the case the unnamed copy variant exists for. Kept that way on
    // purpose so both variants are previewable from the demo tokens.
    name: 'Crestline Roofing',
    expiresAt: new Date(Date.now() + 5 * 86400000).toISOString(),
    businessName: 'Crestline Roofing',
    website: {
      domain: 'crestline-roofing.foundlio.com',
      screenshotUrl: '/demo-shots/crestline-roofing.png',
      previewUrl: 'http://localhost:3003/demos',
      businessName: 'Crestline Roofing',
      businessPhone: '(608) 555-0142',
      trade: 'roofing',
      city: 'Madison',
      siteCategory: 'weak_site',
      oldSiteUrl: 'crestlineroofing.example',
    },
  },
};

/* ------------------------------------------------------------------ */
/* Copy                                                                */
/* ------------------------------------------------------------------ */

/** One line per plan, in the voice used on the invite. */
export const PLAN_COPY: Record<PlanId, { label: string; fit: string }> = {
  starter: {
    label: 'A professional website, kept online and current.',
    fit: 'Website essentials',
  },
  professional: {
    label: 'The website plus recurring local visibility work.',
    fit: 'Best for most businesses',
  },
  growth: {
    label: 'Active monthly growth, content and follow-up.',
    fit: 'For businesses ready to push',
  },
};

/**
 * The trade, said the way the owner would say it.
 *
 * The five keyword branches exist because the generator used to send the
 * narrow ASSET-bundle key ("landscaping", "cleaning"), and those five are the
 * only keys that happened to read like a trade. Everything else fell through
 * to the generic "home service" — which is how a tree service's own claim page
 * greeted it with "HOME SERVICE" in the eyebrow and "home service" in the JOB
 * SHEET's TRADE field (fnd-5uj1). The generator now sends the real registry
 * label ("Tree Service", "Junk Removal"), so an unrecognised string is far more
 * likely to be a trade we simply have no keyword for than to be noise: say it
 * back, lowercased, instead of erasing it.
 *
 * "home service" survives only for a genuinely empty trade.
 */
export function tradeName(trade?: string) {
  const raw = trade?.trim() ?? '';
  const normalized = raw.toLowerCase();
  if (normalized.includes('plumb')) return 'plumbing';
  if (normalized.includes('roof')) return 'roofing';
  if (normalized.includes('hvac') || normalized.includes('heating'))
    return 'HVAC';
  if (normalized.includes('electric')) return 'electrical';
  if (normalized.includes('landscap')) return 'landscaping';
  if (!normalized || normalized === 'generic' || normalized === 'home service'
    || normalized === 'home services') return 'home service';
  // A label, not a slug: "tree-service" arrives as "tree service".
  return normalized.replace(/[-_]+/g, ' ');
}

/**
 * The same trade said as the people who do it — "plumbing" → "plumbers".
 *
 * `tradeName` names the work ("here is a plumbing website"); this names the
 * audience ("I build these for plumbers in Madison"), which is what the copy
 * needs when there is no owner name to open on.
 */
export function tradeAudience(trade?: string) {
  switch (tradeName(trade)) {
    case 'plumbing':
      return 'plumbers';
    case 'roofing':
      return 'roofers';
    case 'HVAC':
      return 'HVAC companies';
    case 'electrical':
      return 'electricians';
    case 'landscaping':
      return 'landscapers';
    case 'home service':
      return 'home service businesses';
    default:
      // "tree service" -> "tree service businesses". Clumsier than
      // "landscapers", but it is at least the trade they actually run.
      return `${tradeName(trade)} businesses`;
  }
}

export function normalizeExternalUrl(value?: string) {
  if (!value) return undefined;
  return /^https?:\/\//i.test(value) ? value : `https://${value}`;
}

/** Invites created from scraped data often carry a synthetic address. */
export function usableInviteEmail(value?: string) {
  if (!value || value.endsWith('@placeholder.foundlio.com')) return '';
  return value;
}

/* ------------------------------------------------------------------ */
/* Primitives                                                          */
/* ------------------------------------------------------------------ */

/**
 * Work Order form field: `.wo-field` label + control + hint/error, on
 * `--paper` inputs (`.wo-input`, defined in `wo.css`). Only used by
 * `ClaimWizard`, which always renders inside `.wo-page`.
 */
export const fieldClass = 'wo-input';

export function Field({
  label,
  hint,
  error,
  htmlFor,
  children,
}: {
  label: string;
  hint?: React.ReactNode;
  error?: string;
  htmlFor?: string;
  children: React.ReactNode;
}) {
  return (
    <div className="wo-field">
      <label htmlFor={htmlFor}>{label}</label>
      <div className="wo-field-control">{children}</div>
      {error ? (
        <p role="alert" className="wo-field-error">
          {error}
        </p>
      ) : hint ? (
        <p className="wo-field-hint">{hint}</p>
      ) : null}
    </div>
  );
}

/** One tile of a `.wo-tiles` group — the contact-preference picker. */
export function ChoiceTile({
  title,
  body,
  selected,
  onSelect,
}: {
  title: string;
  body: string;
  selected: boolean;
  onSelect: () => void;
}) {
  return (
    <button
      type="button"
      aria-pressed={selected}
      onClick={onSelect}
      className="wo-tile"
    >
      <span className="wo-tile-title">{title}</span>
      <span className="wo-tile-body">{body}</span>
    </button>
  );
}

export function GoogleMark() {
  return (
    <svg aria-hidden="true" viewBox="0 0 24 24" className="h-5 w-5">
      <path
        fill="#4285F4"
        d="M21.6 12.23c0-.71-.06-1.39-.18-2.05H12v3.87h5.38a4.6 4.6 0 0 1-2 3.02v2.51h3.24c1.9-1.75 2.98-4.33 2.98-7.35Z"
      />
      <path
        fill="#34A853"
        d="M12 22c2.7 0 4.98-.9 6.63-2.42l-3.24-2.51c-.9.6-2.05.96-3.39.96-2.61 0-4.82-1.76-5.61-4.13H3.04v2.6A10 10 0 0 0 12 22Z"
      />
      <path
        fill="#FBBC05"
        d="M6.39 13.9A6 6 0 0 1 6.08 12c0-.66.11-1.3.31-1.9V7.5H3.04A10 10 0 0 0 2 12c0 1.61.39 3.14 1.04 4.5l3.35-2.6Z"
      />
      <path
        fill="#EA4335"
        d="M12 5.97c1.47 0 2.79.51 3.83 1.5l2.87-2.88A9.63 9.63 0 0 0 12 2a10 10 0 0 0-8.96 5.5l3.35 2.6C7.18 7.73 9.39 5.97 12 5.97Z"
      />
    </svg>
  );
}
